import type { InputHTMLAttributes, ReactNode } from 'react';

import { cn } from '../lib/cn';

export type TInputSize = 'md' | 'row' | 'sm';

interface IInputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'size'> {
  invalid?: boolean;
  mono?: boolean;
  size?: TInputSize;
  leading?: ReactNode;
  trailing?: ReactNode;
  bare?: boolean;
}

const SIZE: Record<TInputSize, { box: string; text: string }> = {
  md: { box: 'h-control px-input gap-3', text: 'text-body' },
  row: { box: 'h-control-sm px-inset gap-2', text: 'text-body' },
  sm: { box: 'h-control-xs px-inset gap-2', text: 'text-meta' },
};

export const Input = ({
  invalid,
  mono,
  size = 'md',
  leading,
  trailing,
  bare,
  className,
  ...rest
}: IInputProps) => (
  <div
    className={cn(
      'flex items-center',
      SIZE[size].box,
      !bare && [
        'rounded-control border bg-field',
        'focus-within:border-border-focus focus-within:ring-3 focus-within:ring-focus',
        invalid ? 'border-danger' : 'border-border-raised',
        rest.disabled && 'border-border-muted bg-field-muted',
      ],
      className,
    )}
  >
    {leading && <span className="flex shrink-0 text-faint">{leading}</span>}
    <input
      aria-invalid={invalid || undefined}
      className={cn(
        // `shadow-none` beside `outline-none`: base.css draws the halo as a box-shadow, so without this the inner input draws a second ring inside the wrapper's.
        'min-w-0 grow bg-transparent text-primary outline-none focus-visible:shadow-none',
        SIZE[size].text,
        mono && 'font-mono text-data',
        'disabled:text-muted',
        // WebKit gives a `search` input a native clear button that ignores every token here.
        '[&::-webkit-search-cancel-button]:hidden',
      )}
      {...rest}
    />
    {trailing && <span className="flex shrink-0">{trailing}</span>}
  </div>
);
